import { useState } from "react";
import type { Media } from "@shared";
import { DICTIONARIES, format, type GuestCopy } from "../web/src/lib/i18n.js";

interface Props {
  readonly media: Media;
  readonly showGuestName: boolean;
  readonly onOpen: (media: Media) => void;
  /** Overrides blurriness — a moderation queue shows the real thing. */
  readonly reveal?: boolean;
  readonly className?: string;
  /**
   * Selection mode for the guest download. When true, tapping the tile toggles
   * the selection instead of opening the lightbox, and a real checkbox appears.
   */
  readonly selectable?: boolean;
  readonly selected?: boolean;
  readonly onToggle?: (media: Media) => void;
  /** The guest's dictionary. Host surfaces omit it and stay English. */
  readonly copy?: GuestCopy;
}

/**
 * One tile of media.
 *
 * Videos autoplay muted and loop because a wall with a frozen first frame looks
 * broken, and a projector is always muted anyway. `preload="metadata"` keeps a
 * gallery of two hundred videos from pulling two hundred files down the wire
 * before anybody scrolls.
 *
 * Blurred media renders its own placeholder rather than a CSS filter: a filter
 * still downloads the photograph, and a host who chose to blur something meant
 * for the gallery to not show it clearly.
 */
export function MediaTile({
  media,
  showGuestName,
  onOpen,
  reveal,
  className,
  selectable,
  selected,
  onToggle,
  copy = DICTIONARIES.en,
}: Props) {
  const [failed, setFailed] = useState(false);
  // Photos fade in from a soft blur once their bytes are here, instead of
  // painting in stripes on venue Wi-Fi. A cached image is complete before
  // React attaches onLoad, so the ref checks for that too.
  const [loaded, setLoaded] = useState(false);
  const hidden = media.blurred && !reveal;
  const label = media.caption || media.aiCaption || media.guestName || copy.photo;

  return (
    <figure
      className={`tile${hidden ? " tile-hidden" : ""}${media.pinned ? " tile-pinned" : ""}${
        selectable ? " tile-selectable" : ""
      }${selected ? " tile-selected" : ""}${loaded || failed || hidden ? " is-loaded" : ""}${className ? ` ${className}` : ""}`}
    >
      {/*
        A real checkbox, not a decorated div: selection is the one control a
        screen reader and a switch device both have to be able to reach. It sits
        outside the open button because a nested input inside a button is
        invalid HTML and behaves differently in every browser.
      */}
      {selectable ? (
        <label className="tile-select">
          <input
            type="checkbox"
            checked={Boolean(selected)}
            onChange={() => onToggle?.(media)}
            aria-label={format(copy.selectPhoto, { name: label })}
          />
          <span className="tile-select-mark" aria-hidden="true" />
        </label>
      ) : null}

      <button
        type="button"
        className="tile-open"
        onClick={() => (selectable ? onToggle?.(media) : onOpen(media))}
        aria-pressed={selectable ? Boolean(selected) : undefined}
      >
        {hidden ? (
          <span className="tile-hidden-label">{copy.hiddenByHost}</span>
        ) : failed ? (
          <span className="tile-hidden-label">{copy.couldNotLoad}</span>
         ) : media.mimeType.startsWith("audio/") ? (
           <div className="tile-audio">
             <audio src={media.displayUrl} controls preload="metadata" />
           </div>
        ) : media.mimeType.startsWith("video/") ? (
          <video
            src={media.displayUrl}
            poster={media.thumbUrl || undefined}
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
            onLoadedData={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        ) : (
          <img
            // The tile is a few hundred pixels wide; downloading the original
            // was the largest avoidable cost on a phone at a venue.
            src={media.thumbUrl || media.displayUrl}
            alt={media.caption || media.aiCaption || media.guestName || copy.eventPhoto}
            loading="lazy"
            decoding="async"
            ref={(node) => {
              if (node?.complete && node.naturalWidth > 0) setLoaded(true);
            }}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        )}
      </button>

      {media.pinned ? <span className="tile-badge" title={copy.pinnedToWall}>{copy.pinned}</span> : null}

      {/* Only when there is something to say: a guest who left no name used to
          get an empty caption strip under their photo. */}
      {hidden || (!media.caption && !(showGuestName && media.guestName)) ? null : (
        <figcaption className="tile-caption">
          {media.caption ? <span className="tile-text">{media.caption}</span> : null}
          {showGuestName && media.guestName ? (
            <span className="tile-by">{media.guestName}</span>
          ) : null}
        </figcaption>
      )}
    </figure>
  );
}
